// Live SLO -> error-budget + burn-rate calculator. The page ships a fully
// server-rendered default state, so this only upgrades it to recompute on
// input. Math mirrors the Rust side so hydration never changes the numbers.
import { humanDuration, multiple, percent } from "./_duration.js";
import { t } from "./_i18n.js";
import { toolUsed } from "./_tool_event.js";

const TOOL = "error-budget";
function downtime(pct, windowSecs) {
  return windowSecs * (1 - pct / 100);
}

function burnRate(measured, slo) {
  const allowed = 1 - slo / 100;
  if (allowed <= 0) return measured >= 100 ? 0 : Infinity;
  return Math.max((1 - measured / 100) / allowed, 0);
}

function budgetResult(slo, measured, windowSecs) {
  const total = downtime(slo, windowSecs);
  const used = downtime(measured, windowSecs);
  const remaining = Math.max(total - used, 0);
  const rate = burnRate(measured, slo);

  let verdict;
  let state;
  if (measured >= 100) {
    verdict = t("eb-verdict-intact");
    state = "ok";
  } else if (!Number.isFinite(rate)) {
    verdict = t("eb-verdict-no-budget");
    state = "warn";
  } else if (rate < 1) {
    verdict = t("eb-verdict-under", { rate: multiple(rate) });
    state = "ok";
  } else if (Math.abs(rate - 1) < 0.001) {
    verdict = t("eb-verdict-exact");
    state = "on";
  } else {
    verdict = t("eb-verdict-over", { time: humanDuration(windowSecs / rate) });
    state = "warn";
  }

  return {
    total: humanDuration(total),
    used: humanDuration(used),
    remaining: humanDuration(remaining),
    burn: multiple(rate),
    verdict,
    state,
  };
}

// Chart geometry — kept byte-identical to the Rust burn_chart() so the first
// client redraw lands on the server-rendered pixels.
const CHART_X0 = 40;
const CHART_X1 = 568;
const CHART_Y0 = 16;
const CHART_Y1 = 190;

function chartX(frac) {
  return CHART_X0 + frac * (CHART_X1 - CHART_X0);
}

function chartY(frac) {
  return CHART_Y0 + (1 - frac) * (CHART_Y1 - CHART_Y0);
}

function point(x, y) {
  return `${x.toFixed(1)},${y.toFixed(1)}`;
}

function buildChart(slo, measured, windowSecs) {
  const rate = burnRate(measured, slo);
  const over = !Number.isFinite(rate) || rate > 1;
  if (over) {
    const tStar = Number.isFinite(rate) ? 1 / rate : 0;
    const crossX = chartX(tStar);
    return {
      linePoints: `${point(chartX(0), chartY(1))} ${point(crossX, chartY(0))} ${point(chartX(1), chartY(0))}`,
      dotX: crossX.toFixed(1),
      dotY: chartY(0).toFixed(1),
      labelX: crossX.toFixed(1),
      labelY: (chartY(0) - 8).toFixed(1),
      labelAnchor: "middle",
      labelText: Number.isFinite(rate) ? t("eb-chart-gone", { time: humanDuration(windowSecs / rate) }) : t("eb-chart-no-budget"),
      state: "warn",
    };
  }
  const endFrac = 1 - rate;
  const endY = chartY(endFrac);
  return {
    linePoints: `${point(chartX(0), chartY(1))} ${point(chartX(1), endY)}`,
    dotX: CHART_X1.toFixed(1),
    dotY: endY.toFixed(1),
    labelX: (CHART_X1 + 6).toFixed(1),
    labelY: endY.toFixed(1),
    labelAnchor: "start",
    labelText: t("eb-chart-left", { pct: percent(Math.round(endFrac * 100)) }),
    state: "ok",
  };
}

const CHART_STATES = ["ok", "warn"];

function drawChart(chart, c) {
  c.line.setAttribute("points", chart.linePoints);
  c.dot.setAttribute("cx", chart.dotX);
  c.dot.setAttribute("cy", chart.dotY);
  c.label.setAttribute("x", chart.labelX);
  c.label.setAttribute("y", chart.labelY);
  c.label.setAttribute("text-anchor", chart.labelAnchor);
  c.label.textContent = chart.labelText;
  for (const s of CHART_STATES) {
    c.line.classList.toggle(`burn-chart__line--${s}`, s === chart.state);
    c.dot.classList.toggle(`burn-chart__dot--${s}`, s === chart.state);
  }
}

const VERDICT_STATES = ["ok", "warn", "on"];

function recompute(sloInput, measuredInput, windowGroup, ctx) {
  const slo = parseFloat(sloInput.value);
  const measured = parseFloat(measuredInput.value);
  const sloValid = Number.isFinite(slo) && slo >= 0 && slo <= 100;
  const measuredValid = Number.isFinite(measured) && measured >= 0 && measured <= 100;
  sloInput.setAttribute("aria-invalid", sloValid ? "false" : "true");
  measuredInput.setAttribute("aria-invalid", measuredValid ? "false" : "true");

  if (!sloValid || !measuredValid) {
    for (const node of ctx.budgetNodes) node.textContent = "—";
    ctx.burnNode.textContent = "—";
    ctx.verdictNode.textContent = "";
    return;
  }

  const active = windowGroup.querySelector(".is-active");
  const windowSecs = parseFloat(active ? active.dataset.secs : "2592000");
  const r = budgetResult(slo, measured, windowSecs);

  for (const node of ctx.budgetNodes) node.textContent = r[node.dataset.budget];
  ctx.burnNode.textContent = r.burn;
  ctx.verdictNode.textContent = r.verdict;
  for (const s of VERDICT_STATES) {
    ctx.verdictNode.classList.toggle(`tool-verdict--${s}`, s === r.state);
  }

  if (ctx.chart) {
    drawChart(buildChart(slo, measured, windowSecs), ctx.chart);
    ctx.crosshair.rate = burnRate(measured, slo);
    ctx.crosshair.windowSecs = windowSecs;
    if (active) ctx.chart.window.textContent = active.textContent;
  }
}

// Crosshair: read budget remaining at the day under the cursor. The line is a
// deterministic function of the inputs, so this reads it rather than a dataset.
function wireCrosshair(svg, ctx) {
  const { group, line, label } = ctx.crosshairEls;
  const hit = svg.querySelector("#burn-hit");
  if (!group || !line || !label || !hit) return;

  hit.addEventListener("mousemove", (e) => {
    const box = svg.getBoundingClientRect();
    const svgX = ((e.clientX - box.left) / box.width) * 640;
    const frac = Math.min(Math.max((svgX - CHART_X0) / (CHART_X1 - CHART_X0), 0), 1);
    const rate = ctx.crosshair.rate;
    const remaining = Number.isFinite(rate) ? Math.max(1 - rate * frac, 0) : 0;
    const x = chartX(frac);
    const days = Math.round((frac * ctx.crosshair.windowSecs) / 86_400);
    line.setAttribute("x1", x.toFixed(1));
    line.setAttribute("x2", x.toFixed(1));
    label.setAttribute("x", Math.min(Math.max(x, 60), 548).toFixed(1));
    label.textContent = t("eb-chart-day", { day: days, pct: percent(Math.round(remaining * 100)) });
    group.classList.add("is-on");
  });
  hit.addEventListener("mouseleave", () => group.classList.remove("is-on"));
}

// Value wheel: click a neighbour to snap the input to it and roll the ghosts.
// SLO snaps through its preset list (data-values); measured steps by a fixed
// amount (data-step). The centre stays a plain, typeable input.
function round2(v) {
  return Math.round(v * 100) / 100;
}

function fmt(v) {
  return `${parseFloat(v.toFixed(2))}`;
}

function setupWheel(wheel, run) {
  const input = wheel.querySelector("input");
  const ghosts = wheel.querySelector(".wheel__ghosts");
  if (!input || !ghosts) return;

  const values = wheel.dataset.values
    ? wheel.dataset.values.split(",").map(Number)
    : null;
  const step = wheel.dataset.step ? parseFloat(wheel.dataset.step) : null;
  const cells = [-2, -1, 1, 2].map((off) => [
    off,
    ghosts.querySelector(`[data-slot="${off}"]`),
  ]);
  const canAnimate =
    typeof input.animate === "function" &&
    !(window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches);

  const nearest = (v) =>
    values.reduce(
      (best, p, i) => (Math.abs(p - v) < Math.abs(values[best] - v) ? i : best),
      0,
    );

  function neighbour(v, off) {
    if (values) {
      const j = nearest(v) + off;
      return j >= 0 && j < values.length ? values[j] : null;
    }
    const nv = round2(v + off * step);
    return nv >= 0 && nv <= 100 ? nv : null;
  }

  function render() {
    const v = parseFloat(input.value);
    for (const [off, cell] of cells) {
      const nv = Number.isFinite(v) ? neighbour(v, off) : null;
      cell.classList.toggle("is-empty", nv === null);
      cell.disabled = nv === null;
      cell.textContent = nv === null ? "" : fmt(nv);
      cell.setAttribute("aria-label", nv === null ? "" : `Set to ${fmt(nv)}%`);
    }
  }

  let ghostAnim = null;
  let centerAnim = null;
  function roll(dir) {
    if (!canAnimate) return;
    if (ghostAnim) ghostAnim.cancel();
    if (centerAnim) centerAnim.cancel();
    ghostAnim = ghosts.animate(
      [{ transform: `translateX(${dir * 60}px)` }, { transform: "translateX(0)" }],
      { duration: 300, easing: "cubic-bezier(0.2, 0, 0, 1)" },
    );
    centerAnim = input.animate(
      [{ transform: "scale(0.95)" }, { transform: "scale(1)" }],
      { duration: 200, easing: "cubic-bezier(0.2, 0, 0, 1)" },
    );
  }

  for (const [off, cell] of cells) {
    cell.addEventListener("click", () => {
      const v = parseFloat(input.value);
      if (!Number.isFinite(v)) return;
      const nv = neighbour(v, off);
      if (nv === null) return;
      input.value = fmt(nv);
      render();
      roll(off > 0 ? 1 : -1);
      run();
    });
  }

  // Scroll the number strip to step one value per notch (throttled), using
  // whichever scroll axis dominates so a plain wheel or a trackpad both work.
  const viewport = wheel.querySelector(".wheel__viewport");
  let lastStep = 0;
  viewport.addEventListener(
    "wheel",
    (e) => {
      const v = parseFloat(input.value);
      if (!Number.isFinite(v)) return;
      const d = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (d === 0) return;
      e.preventDefault();
      if (e.timeStamp - lastStep < 80) return;
      const dir = d > 0 ? 1 : -1;
      const nv = neighbour(v, dir);
      if (nv === null) return;
      lastStep = e.timeStamp;
      input.value = fmt(nv);
      render();
      roll(dir);
      run();
    },
    { passive: false },
  );

  input.addEventListener("input", render);
  render();
}

function init() {
  const sloInput = document.getElementById("slo-input");
  const measuredInput = document.getElementById("measured-input");
  const windowGroup = document.querySelector('[data-group="window"]');
  if (!sloInput || !measuredInput || !windowGroup) return;

  const ctx = {
    budgetNodes: document.querySelectorAll("[data-budget]"),
    burnNode: document.getElementById("burn-rate"),
    verdictNode: document.getElementById("verdict"),
    crosshair: { rate: 0, windowSecs: 2_592_000 },
  };

  const svg = document.querySelector(".burn-chart");
  const line = document.getElementById("burn-line");
  const dot = document.getElementById("burn-dot");
  const label = document.getElementById("burn-label");
  const windowLabel = document.getElementById("burn-window");
  if (svg && line && dot && label && windowLabel) {
    ctx.chart = { line, dot, label, window: windowLabel };
    ctx.crosshairEls = {
      group: document.getElementById("burn-cross"),
      line: document.querySelector(".burn-chart__cross-line"),
      label: document.getElementById("burn-cross-label"),
    };
    wireCrosshair(svg, ctx);
  }

  const recalc = () => recompute(sloInput, measuredInput, windowGroup, ctx);
  const run = () => {
    recalc();
    toolUsed(TOOL);
  };

  sloInput.addEventListener("input", run);
  measuredInput.addEventListener("input", run);

  for (const wheel of document.querySelectorAll(".wheel")) setupWheel(wheel, run);

  for (const btn of windowGroup.querySelectorAll(".tool-preset")) {
    btn.addEventListener("click", () => {
      for (const b of windowGroup.querySelectorAll(".tool-preset")) {
        const on = b === btn;
        b.classList.toggle("is-active", on);
        b.setAttribute("aria-pressed", on);
      }
      run();
    });
  }

  // Hydration, not use.
  recalc();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
